import type { LoaderFunctionArgs } from '@remix-run/node';

import { Links, Meta, Outlet, Scripts, json, useLoaderData, useLocation } from '@remix-run/react';
import { IntegrationFullStory, IntegrationGoogleTag } from './modules/integration';
import { useInitializeLanguage, useInitializeMaintainMode } from './modules/apps';
import { WebsocketProvider } from './modules/apps/contexts/WebsocketProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useConvertPathnameToTitle } from '~/presentation/common';
import { mappingEnvironments } from './server/utils/environment';
import { toInlineScriptJson } from '~/utils/toInlineScriptJson';
import { createHead } from 'remix-island';
import { Fragment } from 'react';

// import polarisStyles from '~/assets/styles/polaris.css?url'; // eslint-disable-line import/no-unresolved
import baseStyles from '~/assets/styles/base.css?url'; // eslint-disable-line import/no-unresolved

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 60 * 24 * 7, // 7 days
    },
  },
});

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const isMaintenance = url.searchParams.has('maintenance_mode');
  const env = mappingEnvironments(process.env);
  const shopifyDomain = url.searchParams.get('shop');
  const locale = url.searchParams?.get('locale');

  return json({
    env: {
      ...env,
      APP_GTM: process.env.APP_GTM,
      APP_GA_MEASUREMENT_ID: process.env.APP_GA_MEASUREMENT_ID,
    },
    locale,
    shopifyDomain,
    isMaintenance,
    isDevelopmentMode: process.env.NODE_ENV === 'development',
  });
};

export default function App() {
  const { env, locale, isMaintenance, isDevelopmentMode } = useLoaderData<typeof loader>();

  useInitializeLanguage({ locale });
  useInitializeMaintainMode({ isMaintenance });

  return (
    <Fragment>
      {isDevelopmentMode && <HeadComponent />}

      <QueryClientProvider client={queryClient}>
        <WebsocketProvider options={{ reconnectEnabled: true }}>
          <Outlet />
        </WebsocketProvider>
      </QueryClientProvider>

      <Scripts />

      <IntegrationFullStory />
      <IntegrationGoogleTag
        APP_GTM={env.APP_GTM}
        APP_GA_MEASUREMENT_ID={env.APP_GA_MEASUREMENT_ID}
      />
    </Fragment>
  );
}

export const HeadComponent = createHead(() => {
  const { env, shopifyDomain } = useLoaderData<typeof loader>();
  const { convertPathnameToTitle } = useConvertPathnameToTitle();
  const { pathname } = useLocation();
  // `shopifyDomain` is the raw `?shop=` param, so it must stay JSON data, never script source.
  const bootstrapScript = `
          window.__GEMX_ENVIRONMENT__ = ${toInlineScriptJson(env)};
          window.__SHOPIFY_CONFIG__ = ${toInlineScriptJson({ shopifyDomain })};`;

  return (
    <>
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width,initial-scale=1" />
      <link rel="preconnect" href="https://cdn.shopify.com/" />
      <title>{convertPathnameToTitle(pathname)}</title>
      <Meta />
      <Links />
      {/* <link rel="preload" href={polarisStyles} as="style" /> */}
      <link rel="preload" href="/polaris.css" as="style" />
      <script dangerouslySetInnerHTML={{ __html: bootstrapScript }} />
    </>
  );
});

export const GemXStyles = createHead(() => {
  return (
    <>
      {/* <link rel="stylesheet" href={polarisStyles} /> */}
      <link rel="stylesheet" href="/polaris.css" />
      <link rel="stylesheet" href={baseStyles} />
    </>
  );
});
